import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { FormDefinition, FormSection } from "../types/formTypes";
import { Logger } from "@/lib/logger/Logger";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";
import { showNotification } from "../../../store/Central/selectors";
import { useEffect } from "react";
import { supabase } from "@/lib/supabase/supabaseClient";
import { Tables } from "@/lib/supabase";
import { useParams, useNavigate } from "react-router-dom";
import { useCentralStore } from "../../../store/Central";
import { useAutoSave } from "../../../hooks/useAutosave/useAutosave";
import { Json } from "@/lib/supabase/supabaseTypesImport";
import { ExtendedFormTemplate } from "../types/formTypes";
import { FormRule } from "../types/ruleTypes";

// Save status states
export type SaveStatus = "unsaved" | "saving" | "saved" | "error";

export function useFormBuilder() {
	const { organizationId } = useCentralStore((state) => ({
		organizationId: state.organization?.id,
	}));
	const { formId } = useParams();
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(true);
	const [forms, setForms] = useState<Record<string, ExtendedFormTemplate>>(
		{}
	);
	const [activeForm, setActiveForm] = useState<ExtendedFormTemplate | null>(
		null
	);
	const [activeSection, setActiveSection] = useState<string | null>(null);
	const [isPendingChanges, setIsPendingChanges] = useState(false);
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");

	// Keep track of last saved time
	const [lastSaved, setLastSaved] = useState<Date | null>(null);

	useEffect(() => {
		if (organizationId) {
			fetchForms(organizationId);
		}
	}, [organizationId]);

	// Convert raw database form into ExtendedFormTemplate with parsed sections
	const parseFormDefinition = (
		form: Tables<"forms">
	): ExtendedFormTemplate => {
		return {
			...form,
			definition: {
				...(form as any).definition,
				sections: (form as any).definition.sections || ([] as Json),
			},
			rules: (form as any).rules || ([] as FormRule[]),
		};
	};

	const fetchForms = async (organizationId: string) => {
		setIsLoading(true);

		try {
			const { data, error } = await supabase
				.from(SupabaseTableEnum.FORMS)
				.select("*")
				.eq("organization_id", organizationId);

			if (error) {
				Logger.error("Error fetching forms:", {}, error);
				showNotification({
					message: "Fehler beim Laden der Formulare",
					type: "error",
					description:
						"Es ist ein Fehler aufgetreten, während die Formulare geladen wurden",
				});
			} else {
				// Parse the forms and set up the state
				if (data && data.length > 0) {
					const parsedForms = data.reduce(
						(acc, form) => {
							acc[form.id] = parseFormDefinition(form);
							return acc;
						},
						{} as Record<string, ExtendedFormTemplate>
					);

					setForms(parsedForms);

					// Set active form based on URL parameter
					// Only set an active form if we're on the form edit page with a valid formId
					if (formId && parsedForms[formId]) {
						setActiveForm(parsedForms[formId]);
						// Set the first section as active if none is selected yet
						if (
							!activeSection &&
							parsedForms[formId].definition.sections.length > 0
						) {
							setActiveSection(
								parsedForms[formId].definition.sections[0].id
							);
						}
					} else if (formId) {
						// Invalid form ID in URL
						setActiveForm(null);
					} else {
						// We're on the forms list page, don't set an active form
						setActiveForm(null);
					}

					// Initialize as saved since we just loaded from DB
					setSaveStatus("saved");
				} else {
					// No forms found
					setForms({});
					setActiveForm(null);
				}
			}
		} catch (error) {
			Logger.error("Error in fetchForms:", {}, error);
		} finally {
			setIsLoading(false);
		}
	};

	// Create a new form template
	const createNewForm = async () => {
		if (!organizationId) {
			showNotification({
				message: "Organisation ID fehlt",
				type: "error",
			});
			return;
		}

		setIsLoading(true);

		try {
			// Create the default section
			const defaultSection: FormSection = {
				id: uuidv4(),
				title: "Abschnitt 1",
				fields: [],
			};

			// Convert sections array to a safe JSON structure
			const sectionsJson = JSON.parse(
				JSON.stringify({ sections: [defaultSection] })
			);

			// Create a new form with required fields
			const newForm = {
				title: "Neues Formular",
				description: "",
				organization_id: organizationId,
				created_at: new Date().toISOString(),
				modified_at: new Date().toISOString(),
				definition: sectionsJson as Json,
				bg_color: "green-50",
			};

			// Insert the new form into the database
			const { data, error } = await supabase
				.from(SupabaseTableEnum.FORMS)
				.insert(newForm)
				.select();

			if (error) {
				Logger.error("Error creating new form:", {}, error);
				showNotification({
					message: "Fehler beim Erstellen des Formulars",
					type: "error",
				});
			} else if (data && data.length > 0) {
				// Parse the new form
				const parsedForm = parseFormDefinition(data[0]);

				// Update the local state
				setForms((prev) => ({
					...prev,
					[parsedForm.id]: parsedForm,
				}));

				// Set as active form
				setActiveForm(parsedForm);
				setActiveSection(defaultSection.id);

				// Navigate to the new form
				navigate(`/auftragsformulare/${parsedForm.id}`);

				showNotification({
					message: "Neues Formular erstellt",
					type: "success",
				});
			}
		} catch (error) {
			Logger.error("Error in createNewForm:", {}, error);
		} finally {
			setIsLoading(false);
		}
	};

	// Mark form as changed, triggering autosave
	const markFormAsChanged = () => {
		setIsPendingChanges(true);
		setSaveStatus("unsaved");
	};

	// Update form title, description, or bg_color
	const updateFormMeta = (
		key: "title" | "description" | "bg_color",
		value: string
	) => {
		if (!activeForm) return;

		setActiveForm((prev) => {
			if (!prev) return null;

			return {
				...prev,
				[key]: value,
			};
		});

		markFormAsChanged();
	};

	// Toggle the published state of the form
	const togglePublishState = async (formId?: string) => {
		// Use provided formId or fall back to activeForm.id
		const targetFormId = formId || activeForm?.id;
		if (!targetFormId) return;

		// Get the target form from forms state or activeForm
		const targetForm = formId ? forms[formId] : activeForm;
		if (!targetForm) return;

		try {
			setSaveStatus("saving");

			// Determine the new published state
			const now = new Date().toISOString();
			const newPublishedAt = targetForm.published_at ? null : now;

			// Update local state
			if (formId) {
				// Update in forms state
				setForms((prev) => ({
					...prev,
					[formId]: {
						...prev[formId],
						published_at: newPublishedAt,
						modified_at: now,
					},
				}));
			} else {
				// Update activeForm
				setActiveForm((prev) => {
					if (!prev) return null;

					return {
						...prev,
						published_at: newPublishedAt,
						modified_at: now,
					};
				});
			}

			// Update the database
			const { error } = await supabase
				.from(SupabaseTableEnum.FORMS)
				.update({
					published_at: newPublishedAt,
				})
				.eq("id", targetFormId);

			if (error) {
				Logger.error("Error updating form publish state:", {}, error);
				showNotification({
					message:
						"Fehler beim Veröffentlichen/Zurückziehen des Formulars",
					type: "error",
					description: error.message,
				});
				setSaveStatus("error");
				return;
			}

			// Set as saved
			setSaveStatus("saved");
			setLastSaved(new Date());

			showNotification({
				message: newPublishedAt
					? "Formular wurde veröffentlicht"
					: "Formular wurde zurückgezogen",
				type: "success",
			});
		} catch (error) {
			Logger.error("Error toggling publish state:", {}, error);
			showNotification({
				message: "Fehler beim Ändern des Veröffentlichungsstatus",
				type: "error",
			});
			setSaveStatus("error");
		}
	};

	// Add a new section to the form
	const addSection = () => {
		if (!activeForm) return;

		const newSection: FormSection = {
			id: uuidv4(),
			title: `Abschnitt ${activeForm.definition.sections.length + 1}`,
			fields: [],
		};

		setActiveForm((prev) => {
			if (!prev) return null;

			return {
				...prev,
				definition: {
					...prev.definition,
					sections: [...prev.definition.sections, newSection],
				},
				modified_at: new Date().toISOString(),
			};
		});

		setActiveSection(newSection.id);
		markFormAsChanged();
	};

	// Update a section's properties
	const updateSection = (
		sectionId: string,
		updates: Partial<FormSection>
	) => {
		if (!activeForm) return;

		setActiveForm((prev) => {
			if (!prev) return null;

			return {
				...prev,
				definition: {
					...prev.definition,
					sections: prev.definition.sections.map((section) =>
						section.id === sectionId
							? { ...section, ...updates }
							: section
					),
				},
				modified_at: new Date().toISOString(),
			};
		});

		markFormAsChanged();
	};

	// Delete a section
	const deleteSection = (sectionId: string) => {
		if (!activeForm) return;

		if (activeForm.definition.sections.length <= 1) {
			showNotification({
				message: "Abschnitt kann nicht gelöscht werden",
				type: "error",
			});
			return;
		}

		setActiveForm((prev) => {
			if (!prev) return null;

			const newSections = prev.definition.sections.filter(
				(section) => section.id !== sectionId
			);

			// If the active section is deleted, set the first section as active
			if (activeSection === sectionId) {
				setActiveSection(newSections[0].id);
			}

			return {
				...prev,
				definition: {
					...prev.definition,
					sections: newSections,
				},
				modified_at: new Date().toISOString(),
			};
		});

		markFormAsChanged();
	};

	// Handle section reordering
	const handleSectionReorder = (result: {
		fromIndex: number;
		toIndex: number;
	}) => {
		if (!activeForm) return;

		const { fromIndex, toIndex } = result;

		// Check if dropped in same place
		if (fromIndex === toIndex) {
			return;
		}

		// Reorder the sections
		const sections = Array.from(activeForm.definition.sections || []);
		const [removed] = sections.splice(fromIndex, 1);
		sections.splice(toIndex, 0, removed);

		setActiveForm((prev) => {
			if (!prev) return null;

			return {
				...prev,
				definition: {
					...prev.definition,
					sections,
				},
				modified_at: new Date().toISOString(),
			};
		});

		markFormAsChanged();
	};

	// Prepare form for saving to database
	const prepareFormForSave = (form: ExtendedFormTemplate) => {
		// Create a copy of the form definition (sections only)
		const definitionJson = JSON.parse(
			JSON.stringify({
				sections: form.definition.sections,
			})
		);

		// Convert rules to Json array for database
		const rulesJson = JSON.parse(JSON.stringify(form.rules || []));

		const formToSave = {
			title: form.title,
			description: form.description,
			created_at: form.created_at,
			id: form.id,
			organization_id: form.organization_id,
			modified_at: new Date().toISOString(),
			// Store background color in bg_color column
			bg_color: form?.bg_color || "green-50",
			// Pack the sections into the definition JSON - safe conversion
			definition: definitionJson as Json,
			// Store rules in separate column as array
			rules: rulesJson as Json[],
		};

		return formToSave;
	};

	// Save form template to database
	const saveFormToDatabase = async (): Promise<void> => {
		try {
			if (!activeForm || !activeForm.id) {
				showNotification({
					message: "Kein aktives Formular zum Speichern",
					type: "error",
				});
				setSaveStatus("error");
				return;
			}

			// Update status to saving
			setSaveStatus("saving");

			// Prepare the form for saving
			const formToSave = prepareFormForSave(activeForm);

			// Update the form in the database
			const { data, error } = await supabase
				.from(SupabaseTableEnum.FORMS)
				.update(formToSave)
				.eq("id", activeForm.id)
				.select();

			if (error) {
				Logger.error("Error updating form in database:", {}, error);
				showNotification({
					message: "Fehler beim Speichern des Formulars",
					type: "error",
					description: error.message,
				});
				setSaveStatus("error");
				return;
			}

			// Update the local forms state
			if (data && data.length > 0) {
				const updatedForm = parseFormDefinition(data[0]);

				setForms((prevForms) => ({
					...prevForms,
					[activeForm.id]: updatedForm,
				}));

				setActiveForm(updatedForm);
				setIsPendingChanges(false);
				setSaveStatus("saved");
				setLastSaved(new Date());
			}
		} catch (error) {
			Logger.error("Error saving form to database:", {}, error);
			showNotification({
				message: "Fehler beim Speichern des Formulars",
				type: "error",
			});
			setSaveStatus("error");
		}
	};

	// Set up autosave
	useAutoSave({
		data: activeForm,
		onSave: saveFormToDatabase,
		shouldSave: isPendingChanges,
		delay: 500,
	});

	const isLastForm = Object.keys(forms).length <= 1;

	// Duplicate an existing form. Internal section/field/rule IDs are preserved
	// so rules referencing field IDs continue to resolve within the duplicated form.
	const duplicateForm = async (sourceFormId: string) => {
		if (!organizationId) {
			showNotification({
				message: "Organisation ID fehlt",
				type: "error",
			});
			return;
		}

		const sourceForm = forms[sourceFormId];
		if (!sourceForm) {
			showNotification({
				message: "Formular nicht gefunden",
				type: "error",
			});
			return;
		}

		try {
			const definitionJson = JSON.parse(
				JSON.stringify({
					sections: sourceForm.definition.sections,
				})
			);
			const rulesJson = JSON.parse(
				JSON.stringify(sourceForm.rules || [])
			);

			const newForm = {
				title: `${sourceForm.title} (Kopie)`,
				description: sourceForm.description,
				organization_id: organizationId,
				definition: definitionJson as Json,
				rules: rulesJson as Json[],
				bg_color: sourceForm.bg_color || "green-50",
				published_at: null,
			};

			const { data, error } = await supabase
				.from(SupabaseTableEnum.FORMS)
				.insert(newForm)
				.select();

			if (error) {
				Logger.error("Error duplicating form:", {}, error);
				showNotification({
					message: "Fehler beim Duplizieren des Formulars",
					type: "error",
					description: error.message,
				});
				return;
			}

			if (data && data.length > 0) {
				const parsedForm = parseFormDefinition(data[0]);

				setForms((prev) => ({
					...prev,
					[parsedForm.id]: parsedForm,
				}));

				showNotification({
					message: "Formular dupliziert",
					type: "success",
				});
			}
		} catch (error) {
			Logger.error("Error in duplicateForm:", {}, error);
			showNotification({
				message: "Fehler beim Duplizieren des Formulars",
				type: "error",
			});
		}
	};

	// Delete a form
	const deleteForm = async (formId: string) => {
		// Since we always auto-create a new form anyway we ask not to delete the last one
		// Otherwise user would be confused that they keep deleting forms and a new one shows up again and again
		if (!formId || isLastForm) return;

		try {
			// Delete the form from the database
			const { error } = await supabase
				.from(SupabaseTableEnum.FORMS)
				.delete()
				.eq("id", formId);

			if (error) {
				Logger.error("Error deleting form:", {}, error);
				showNotification({
					message: "Fehler beim Löschen des Formulars",
					type: "error",
					description: error.message,
				});
				return;
			}

			// Update the local state
			setForms((prev) => {
				const newForms = { ...prev };
				delete newForms[formId];
				return newForms;
			});

			showNotification({
				message: "Formular gelöscht",
				type: "success",
			});
		} catch (error) {
			Logger.error("Error in deleteForm:", {}, error);
			showNotification({
				message: "Fehler beim Löschen des Formulars",
				type: "error",
			});
		}
	};

	// Add a new rule
	const addRule = (rule: FormRule) => {
		if (!activeForm) return;

		setActiveForm((prev) => {
			if (!prev) return null;

			return {
				...prev,
				rules: [...(prev.rules || []), rule],
				modified_at: new Date().toISOString(),
			};
		});

		markFormAsChanged();
	};

	// Update an existing rule
	const updateRule = (ruleId: string, updates: Partial<FormRule>) => {
		if (!activeForm) return;

		setActiveForm((prev) => {
			if (!prev) return null;

			return {
				...prev,
				rules: (prev.rules || []).map((rule) =>
					rule.id === ruleId ? { ...rule, ...updates } : rule
				),
				modified_at: new Date().toISOString(),
			};
		});

		markFormAsChanged();
	};

	// Delete a rule
	const deleteRule = (ruleId: string) => {
		if (!activeForm) return;

		setActiveForm((prev) => {
			if (!prev) return null;

			return {
				...prev,
				rules: (prev.rules || []).filter((rule) => rule.id !== ruleId),
				modified_at: new Date().toISOString(),
			};
		});

		markFormAsChanged();
	};

	return {
		activeForm,
		activeSection,
		updateFormMeta,
		addSection,
		updateSection,
		deleteSection,
		handleSectionReorder,
		setActiveSection,
		setActiveForm,
		isPendingChanges,
		saveStatus,
		lastSaved,
		isLoading,
		forms,
		createNewForm,
		deleteForm,
		duplicateForm,
		isLastForm,
		togglePublishState,
		addRule,
		updateRule,
		deleteRule,
	};
}
